import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import AppButton from "../../common/AppButton";
import AppErrorMessage from "../../common/AppErrorMessage";
import LoadingSpinner from "../../common/Loading";
import EyeIcon from "../../IconComp/eye";
import EyeVisibleIcon from "../../IconComp/eyeVisible";

interface IProps {
  showPassword: any;
  value: any;
  handleInputChange: any;
  errors: any;
  setShowPassword: any;
  showConfirmPassword: any;
  setShowConfirmPassword: any;
  handleSubmit: any;
  signupError: any;
}

const SignUpCreatePassword = ({
  showPassword,
  value,
  handleInputChange,
  errors,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  handleSubmit,
  signupError,
}: IProps) => {
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const { isLoading, isLoginError, loginMessage } = useAppSelector(
    (state: any) => state?.auth
  );

  const isMobile = useMediaQuery("(max-width: 1022px)");
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerifySignupData = async () => {
    setIsVerifying(true); // Disable the button
    try {
      await handleSubmit(); // Call api verification function
    } catch (error) {
      console.error("Verification failed", error);
    } finally {
      setIsVerifying(false); // Enable the button after response
    }
  };

  return (
    <>
      <Box sx={{ fontSize: "18px", textAlign: "center", pb: 2 }}>
        Create Password
      </Box>
      <Grid
        container
        spacing={2}
        style={{
          paddingLeft: isMobile ? "0px" : "30px",
          paddingRight: isMobile ? "0px" : "30px",
        }}
      >
        <Grid item xs={12}>
          <TextField
            label="Create Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={value.password}
            onChange={handleInputChange}
            error={Boolean(errors.password)}
            helperText={errors.password}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeVisibleIcon /> : <EyeIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={value.confirmPassword}
            onChange={handleInputChange}
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeVisibleIcon /> : <EyeIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "40px", // Set the total height of the input field
                padding: "0", // Remove default padding
                display: "flex",
                alignItems: "center", // Center the input content
                justifyContent: "center",
              },
              "& .MuiFormLabel-root": {
                top: "-5px", // Adjust label positioning if necessary
              },
              "& .MuiInputBase-input": {
                padding: "0 10px", // Add inner padding for input
                height: "100%", // Fill the container's height
                boxSizing: "border-box",
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box>{signupError && <AppErrorMessage message={signupError} />}</Box>
        </Grid>

        <Grid item xs={12}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <AppButton
              title={
                isVerifying ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Register"
                )
              }
              fullWidth
              variant="contained"
              sx={{ mb: 2 }}
              onClick={handleVerifySignupData}
              disabled={isVerifying} // Disable button when verifying
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default SignUpCreatePassword;
